import React, { useEffect, useState } from "react";
import CaseTab from "../shared/CaseTab";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/shared/context/useAuth";
import { TCaseFilter } from "../data-access/types";

import useGetChamberUsers from "../data-access/useGetChamberUsers";
import { FieldValues } from "react-hook-form";
import { format } from "date-fns";
import useGetActiveAadeCase from "../data-access/useGetActiveAadeCase";
import calculateColumnsPerTab from "../data-access/calculateColumnsPerTab";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import { MRT_PaginationState } from "material-react-table";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import useGetBasicFilters from "../hooks/useGetBasicFilters";
import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";

export default function ActiveAadeCase() {
  const { t } = useTranslation();
  const { isOrgAdmin } = useAuth();

  const [tab, setTab] = useState<Number>(0);
  const [searchFilters, setSearchFilters] = useState<FieldValues>({});
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });

  const basicFilters = useGetBasicFilters();

  const { data: chamberUserResponse, isLoading: isLoadingChamberUsers } =
    useGetChamberUsers(isOrgAdmin);

  useEffect(() => {
    setSearchFilters({});
    setEnableSearch(true);
    setPaginationState({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [tab]);

  const onSuccess = (data: FieldValues) => {
    setSearchFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
      chamberUser: data.chamberUser ? data.chamberUser.value : null,
    });
    setEnableSearch(true);
  };

  const title = t(
    "Εκκρεμείς Υποθέσεις Εκκαθάρισης που διαχειρίζεστε προσωπικά ή άλλοι υπάλληλοι της Υ. Γ.Ε.ΜΗ.",
  );

  const subtitle = t(
    "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν ανατεθεί σε εσάς ή σε άλλον υπάλληλο της Υ. Γ.Ε.ΜΗ. και δεν έχουν ακόμη διεκπεραιωθεί.",
  );

  const activeAadeCaseFilters: TCaseFilter[] = [
    ...basicFilters,
    ...(isOrgAdmin && tab === 1
      ? [
          {
            label: t("Αρμόδιος Χρήστης"),
            component: {
              type: "autoComplete" as const,
              options: chamberUserResponse?.data.chamberUsers,
              loading: isLoadingChamberUsers,
            },
            name: "chamberUser",
          },
        ]
      : []),
  ];

  const { data: activeCasesResponse, isLoading: isLoadingActiveAadeCases } =
    useGetActiveAadeCase({
      filters: searchFilters,
      enabled: enableSearch,
      setEnableSearch: setEnableSearch,
      tab: tab,
      paginationState: paginationState,
    });

  const columns = calculateColumnsPerTab(tab);

  const table = useCustomMaterialTable({
    columns,
    data: activeCasesResponse?.data.rows ?? [],
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    localization: getTableLang(),
    rowCount: activeCasesResponse?.data.rowCount ?? 0,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoadingActiveAadeCases,
      showProgressBars: isLoadingActiveAadeCases,
      pagination: paginationState,
    },
    manualPagination: true,
  });

  return (
    <AadeCaseSearchOrchestrator
      title={title}
      subtitle={subtitle}
      filters={activeAadeCaseFilters}
      tabComponent={
        isOrgAdmin ? <CaseTab value={tab} setValue={setTab} /> : null
      }
      handleSearch={onSuccess}
      table={table}
    />
  );
}
